// src/lib/auth.ts
//
// Authentication utilities for the BENCHMARK Keycloak integration.

export type AccountType =
  | "anonymous"
  | "demo"
  | "admin"
  | "customer"
  | "auditor"
  | "marketing-sales"
  | "sales"
  | "legal"
  | "management";

export function hasGroup(groups: string[] | undefined, group: string): boolean {
  if (!groups || groups.length === 0) return false;
  return groups.includes(group);
}

export function isApprover(groups: string[] | undefined): boolean {
  return hasGroup(groups, "bench-approvers");
}

/** Per docs/operations/iam-model.md §3 the mapping is:
 *  approvers -> admin (highest privilege wins)
 *  auditors -> auditor
 *  marketing -> marketing-sales
 *  sales -> sales
 *  bench-customer -> customer
 *  bench-staff only -> demo
 *  nothing -> anonymous
 */
export function getAccountType(groups: string[] | undefined): AccountType {
  if (!groups || groups.length === 0) return "anonymous";
  if (isApprover(groups)) return "admin";
  if (hasGroup(groups, "bench-auditors")) return "auditor";
  if (hasGroup(groups, "bench-legal")) return "legal";
  if (hasGroup(groups, "bench-management")) return "management";
  if (hasGroup(groups, "bench-marketing")) return "marketing-sales";
  if (hasGroup(groups, "bench-sales")) return "sales";
  if (groups.some((g) => g.startsWith("bench-customer"))) return "customer";
  if (hasGroup(groups, "bench-staff")) return "demo";
  return "anonymous";
}

/** True for any non-anonymous account type. */
export function isStaff(accountType: AccountType): boolean {
  return accountType !== "anonymous" && accountType !== "customer";
}

/** True for accounts that can see (read-only or read-write)
 *  the BENCHMARK admin section. The customer type is bounced
 *  to /portal. */
export function isAdminSideEligible(accountType: AccountType): boolean {
  return (
    accountType === "admin" ||
    accountType === "demo" ||
    accountType === "auditor" ||
    accountType === "marketing-sales" ||
    accountType === "sales" ||
    accountType === "legal" ||
    accountType === "management"
  );
}

export function getKeycloakInitOptions(keycloakUrl: string, realm: string, clientId: string) {
  return {
    url: keycloakUrl,
    realm,
    clientId,
    pkceMethod: "S256" as const,
    checkLoginIframe: false,
    silentCheckSsoRedirectUri:
      typeof window !== "undefined"
        ? window.location.origin + "/silent-check-sso.html"
        : "",
    responseMode: "fragment" as const,
  };
}
